import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'cortona-chat-history';
const ACTIVE_KEY = 'cortona-active-chat';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHistory(history) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
}

function deriveTitle(messages) {
  const firstUser = messages.find(m => m.role === 'user');
  if (!firstUser) return 'New conversation';
  const text = firstUser.content.replace(/[#*_`]/g, '').trim();
  return text.length > 50 ? text.slice(0, 50) + '…' : text;
}

const ConversationContext = createContext(null);

export function ConversationProvider({ children }) {
  const [messages, setMessages] = useState([]);
  const [currentStage, setCurrentStage] = useState('greeting');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [history, setHistory] = useState(() => loadHistory());
  const autoSaveTimer = useRef(null);

  // Auto-save current conversation to history when messages change
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (messages.length < 2 || !activeConversationId) return;
      setHistory(prev => {
        const idx = prev.findIndex(c => c.id === activeConversationId);
        const entry = {
          id: activeConversationId,
          title: deriveTitle(messages),
          messages,
          stage: currentStage,
          updatedAt: Date.now(),
        };
        const next = idx >= 0
          ? prev.map((c, i) => i === idx ? entry : c)
          : [entry, ...prev];
        saveHistory(next);
        return next;
      });
    }, 800);
    return () => clearTimeout(autoSaveTimer.current);
  }, [messages, currentStage, activeConversationId]);

  const startNewConversation = useCallback(() => {
    const id = generateId();
    setActiveConversationId(id);
    setMessages([]);
    setCurrentStage('greeting');
    localStorage.setItem(ACTIVE_KEY, id);
    return id;
  }, []);

  const loadConversation = useCallback((id) => {
    const conv = history.find(c => c.id === id);
    if (!conv) return false;
    setActiveConversationId(id);
    setMessages(conv.messages);
    setCurrentStage(conv.stage || 'greeting');
    localStorage.setItem(ACTIVE_KEY, id);
    return true;
  }, [history]);

  const deleteConversation = useCallback((id) => {
    setHistory(prev => {
      const next = prev.filter(c => c.id !== id);
      saveHistory(next);
      return next;
    });
    if (activeConversationId === id) {
      startNewConversation();
    }
  }, [activeConversationId, startNewConversation]);

  const resetMessages = useCallback(() => {
    startNewConversation();
  }, [startNewConversation]);

  // Ensure there's always an active conversation id
  useEffect(() => {
    if (!activeConversationId) {
      const savedId = localStorage.getItem(ACTIVE_KEY);
      const exists = history.find(c => c.id === savedId);
      if (exists) {
        loadConversation(savedId);
      } else {
        startNewConversation();
      }
    }
  }, []);

  return (
    <ConversationContext.Provider value={{
      messages,
      setMessages,
      currentStage,
      setCurrentStage,
      resetMessages,
      history,
      activeConversationId,
      startNewConversation,
      loadConversation,
      deleteConversation,
    }}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation() {
  const ctx = useContext(ConversationContext);
  if (!ctx) throw new Error('useConversation must be used within ConversationProvider');
  return ctx;
}

export default ConversationContext;
